import { inject } from '@angular/core'
import { ActivatedRouteSnapshot, Params } from '@angular/router'
import { map, take } from 'rxjs/operators'
import { ApplicationArea } from '../shared/services/ppv.api'
import { AuthManager } from '../shared/auth/AuthManager'
import { Observable } from 'rxjs'

export const BreadcrumbResolver = (route: ActivatedRouteSnapshot): Observable<string> => {
   const authMgr = inject(AuthManager)
   const revieweeId = route.paramMap.get('revieweeId')
   const reviewType = route.queryParamMap.get('rt')

   return authMgr.getCurrentUser().pipe(
      take(1),
      map((user) => {
         return 'Performance Review'
      })
   )
}

/**
 * Labels for each review list, keyed by the list's `rt` value.
 */
const REVIEW_LIST_LABELS: Record<string, string> = {
   HM: 'HM Reviews',
   COS: 'COS Reviews',
   RM: 'RM Reviews',
   DO: 'DO Reviews',
   DVM: 'aDVM Reviews',
   TEAM: 'Team Member Reviews',
   CROSSTRAINED: 'Cross-Trained Reviews',
   RECEPTION: 'Reception Reviews',
   CLINICAL: 'Clinical Reviews',
   OPEN: 'Open Cycle Reviews',
}

/**
 * Determines which review list the user came from.
 *
 * On a list page the URL is `/reviews?rt=OPEN`, so `rt` is the list type.
 * On a review page the URL is `/reviews/75797?rt=CLINICAL&from=OPEN`, where
 * `rt` is the reviewee's type and `from` is the list the user came from.
 * Query params are shared across the route tree, so the parent route sees both.
 */
const getReviewListType = (route: ActivatedRouteSnapshot): string | null => {
   return route.queryParamMap.get('from') ?? route.queryParamMap.get('rt')
}

export const ReviewsBreadcrumbResolver = (route: ActivatedRouteSnapshot): string => {
   const listType = getReviewListType(route)

   if (!listType) {
      return 'Reviews'
   }

   return REVIEW_LIST_LABELS[listType] ?? 'Reviews'
}

/**
 * Query params to attach to the review list breadcrumb link,
 * e.g. { rt: 'OPEN' } so the link goes to `/reviews?rt=OPEN`.
 */
export const ReviewsBreadcrumbQueryParamsResolver = (route: ActivatedRouteSnapshot): Params | null => {
   const listType = getReviewListType(route)
   return listType ? { rt: listType } : null
}

export const AdminBreadcrumbResolver = (route: ActivatedRouteSnapshot): string => {
   const section = route.paramMap.get('section')

   switch (section) {
      case 'home':
         return 'Dashboard'
      case 'ai-insights':
         return 'AI Insights'
      case 'analytics':
         return 'Analytics'
      case 'cycle-management':
         return 'Cycle Management'
      case 'user-management':
         return 'User Management'
      case 'export-data':
         return 'Export Data'
      case 'audit-log':
         return 'Audit Log'
      default:
         return 'Admin'
   }
}
